/**
 * Google OAuth Utility – uses Google Identity Services (GIS).
 * Supports both renderButton and OAuth2 popup flow for custom buttons.
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

let oauth2Client: GoogleOAuth2Client | null = null;

/**
 * Load the Google Identity Services script if not already loaded.
 */
export const initGoogleAuth = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!GOOGLE_CLIENT_ID) {
      reject(new Error('Google Client ID not configured'));
      return;
    }

    if (window.google?.accounts) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  });
};

/**
 * Initialize OAuth2 Code Client for popup-based sign in.
 * This is more reliable than One Tap for custom buttons.
 */
export const initOAuth2Client = (
  onSuccess: (credential: string) => void,
  onError?: (error: string) => void
): void => {
  if (!window.google?.accounts?.oauth2) {
    onError?.('Google OAuth2 not loaded');
    return;
  }

  oauth2Client = window.google.accounts.oauth2.initCodeClient({
    client_id: GOOGLE_CLIENT_ID,
    scope: 'openid email profile',
    ux_mode: 'popup',
    callback: async (response: { code?: string; error?: string }) => {
      if (response.error) {
        onError?.(response.error);
        return;
      }
      if (response.code) {
        onSuccess(response.code);
      }
    },
  });
};

/**
 * Trigger OAuth2 popup flow - more reliable than One Tap
 */
export const requestOAuth2Code = (): void => {
  if (oauth2Client) {
    oauth2Client.requestCode();
  }
};

/**
 * Render the official Google "Sign in with Google" button inside `container`.
 * Calls `onCredential` with the id_token when the user completes sign-in.
 */
export const renderGoogleButton = (
  container: HTMLElement,
  onCredential: (credential: string) => void
): void => {
  const doRender = () => {
    window.google!.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: { credential: string }) => {
        onCredential(response.credential);
      },
    });

    window.google!.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: container.offsetWidth || 400,
    });
  };

  if (window.google?.accounts?.id) {
    doRender();
  } else {
    initGoogleAuth().then(doRender).catch(console.error);
  }
};

/**
 * Trigger Google One Tap sign-in from a custom button.
 * Calls `onCredential` on success, `onNotAvailable` if prompt is suppressed.
 */
export const triggerGoogleSignIn = (
  onCredential: (credential: string) => void,
  onNotAvailable?: () => void
): void => {
  const doTrigger = () => {
    window.google!.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: { credential: string }) => {
        onCredential(response.credential);
      },
    });
    window.google!.accounts.id.prompt((notification) => {
      if (notification.isSkippedMoment() || notification.isDismissedMoment()) {
        onNotAvailable?.();
      }
    });
  };

  if (window.google?.accounts?.id) {
    doTrigger();
  } else {
    initGoogleAuth().then(doTrigger).catch(() => onNotAvailable?.());
  }
};

/**
 * Check if Google OAuth is properly configured.
 */
export const isGoogleAuthConfigured = (): boolean => {
  return Boolean(GOOGLE_CLIENT_ID);
};

interface GoogleOAuth2Client {
  requestCode: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void;
          renderButton: (element: HTMLElement, config: object) => void;
          prompt: (momentListener?: (notification: { isSkippedMoment: () => boolean; isDismissedMoment: () => boolean }) => void) => void;
          cancel: () => void;
        };
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode: string;
            callback: (response: { code?: string; error?: string }) => void;
          }) => GoogleOAuth2Client;
        };
      };
    };
  }
}
